import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { authnClient } from '@frontend/auth';
import { PortalUser, locationRedirect, getLastVisitedPage, clearLastVisitedPage, getJTI } from '@frontend/auth-helpers';
import env from '@frontend/env';
import { useHttpSetupOnce, useQueryUser } from '../utils/auth-methods';

type AuthContextType = {
  isAuthenticated: boolean;
  user: PortalUser | null;
  login: () => void;
  logout: () => void;
  handleAuthCallback: () => Promise<void>;
};

const DEFAULT_LANDING_ROUTE = '/location';
const AuthContext = createContext<AuthContextType | undefined>(undefined);

authnClient.changeAuthMethod('okta');

const authConfig: Parameters<typeof authnClient.setConfig>[0] = {
  oktaRedirectUri: env.OKTA_REDIRECT_URI,
  oktaClientID: env.OKTA_CLIENT_ID,
  oktaIssuer: env.OKTA_ISSUER,
};

authnClient.setConfig(authConfig);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = authnClient.isUserAuthenticated();
  const { data: user } = useQueryUser(isAuthenticated);
  const setupHttp = useHttpSetupOnce();

  const setupAuthentication = useCallback(
    (token: string) => {
      if (token) {
        /* Setup http middleware once we have a token */
        setupHttp(token);
      }
    },
    [setupHttp]
  );

  useEffect(() => {
    if (isAuthenticated && user) {
      /* After you are authenticated, setup the http middleware */
      const token = getJTI();
      if (token) setupAuthentication(token);
    }
  }, [isAuthenticated, user, setupAuthentication]);

  const handleAuthCallback = useCallback(async () => {
    /* Ensure that the user is logging in */
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get('code');
    if (!code) return;

    try {
      const tokens = await authnClient.handleCallback();
      const fetchedUser = await authnClient.fetchUser();
      if (fetchedUser) {
        if (tokens.weaveToken) {
          setupAuthentication(tokens.weaveToken); // Pass the token to setupHttp
        }

        locationRedirect(fetchedUser);

        const forwardUrl = getLastVisitedPage('/dashboard');
        const path = forwardUrl === '/' ? '' : forwardUrl;

        clearLastVisitedPage();
        location.href = path || DEFAULT_LANDING_ROUTE;
      }
    } catch (error: any) {
      console.error("Couldn't handle auth callback", error);
    }
  }, [setupAuthentication]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      user: user
        ? {
            ...user,
            firstName: user.firstName ?? 'Unknown',
            lastName: user.lastName ?? 'Unknown',
          }
        : null,

      handleAuthCallback,
      login: () => {
        authnClient.signIn().catch((err: any) => {
          throw new Error("Couldn't login: " + err.message);
        });
      },
      logout: () => {
        authnClient.signOut({ logoutRedirect: window.location.origin }).then(() => {
          location.href = '/sign-in';
        });
      },
    }),
    [isAuthenticated, user, handleAuthCallback]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
};
